const PREFIX = 'motito:checkout-payment-observation:';
let epoch = 0;
let activeAccount: string | null = null;
const knownObservations = new Map<string, CheckoutPaymentObservation>();

export type CheckoutPaymentObservation = {
  version: 1;
  accountId: string;
  checkoutId: string;
  paymentReference: string;
  savedAt: number;
};

function key(accountId: string, checkoutId: string): string {
  return `${PREFIX}${encodeURIComponent(accountId)}:${encodeURIComponent(checkoutId)}`;
}

export function checkoutObservationEpoch(): number { return epoch; }

export function getKnownCheckoutPaymentObservation(accountId: string, checkoutId: string): CheckoutPaymentObservation | undefined {
  return knownObservations.get(key(accountId, checkoutId));
}

export function getKnownCheckoutPaymentObservations(accountId: string): CheckoutPaymentObservation[] {
  return Array.from(knownObservations.values()).filter((value) => value.accountId === accountId);
}

export function invalidateCheckoutObservation(): void { epoch += 1; }

export function clearCheckoutPaymentObservations(): void {
  invalidateCheckoutObservation();
  activeAccount = null;
  knownObservations.clear();
  if (typeof window === 'undefined') return;
  try {
    const keys = Object.keys(window.sessionStorage).filter((value) => value.startsWith(PREFIX));
    keys.forEach((value) => window.sessionStorage.removeItem(value));
  } catch {
    // The epoch still fences in-flight observations when browser storage is unavailable.
  }
}

export function setCheckoutObservationAccount(accountId: string): void {
  if (activeAccount && activeAccount !== accountId) clearCheckoutPaymentObservations();
  activeAccount = accountId;
}

export function saveCheckoutPaymentObservation(
  accountId: string, checkoutId: string, paymentReference: string,
): boolean {
  if (typeof window === 'undefined' || !accountId || !checkoutId || !paymentReference.trim()) return false;
  const observation: CheckoutPaymentObservation = { version: 1, accountId, checkoutId, paymentReference, savedAt: Date.now() };
  // A resettable payment form must not own the only reference when storage fails.
  knownObservations.set(key(accountId, checkoutId), observation);
  try {
    window.sessionStorage.setItem(key(accountId, checkoutId), JSON.stringify(observation));
    return true;
  } catch { return false; }
}

export function readCheckoutPaymentObservation(accountId: string, checkoutId: string):
  { status: 'saved'; value: CheckoutPaymentObservation } | { status: 'none' | 'unavailable' | 'unsupported' } {
  if (typeof window === 'undefined') return { status: 'none' };
  let raw: string | null;
  try { raw = window.sessionStorage.getItem(key(accountId, checkoutId)); }
  catch { return { status: 'unsupported' }; }
  if (!raw) return { status: 'none' };
  try {
    const value = JSON.parse(raw) as Partial<CheckoutPaymentObservation>;
    if (value.version !== 1 || value.accountId !== accountId || value.checkoutId !== checkoutId
      || typeof value.paymentReference !== 'string' || !value.paymentReference.trim()
      || value.paymentReference.length > 255 || typeof value.savedAt !== 'number'
      || !Number.isFinite(value.savedAt)) return { status: 'unavailable' };
    const observation = value as CheckoutPaymentObservation;
    if (!knownObservations.has(key(accountId, checkoutId))) knownObservations.set(key(accountId, checkoutId), observation);
    return { status: 'saved', value: observation };
  } catch { return { status: 'unavailable' }; }
}

export function removeCheckoutPaymentObservation(accountId: string, checkoutId: string): void {
  knownObservations.delete(key(accountId, checkoutId));
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(key(accountId, checkoutId)); } catch {
    // A retained opaque pointer is revalidated before it can be observed again.
  }
}
